import { FC, forwardRef, useMemo, useState } from 'react';

import {
  RiErrorWarningLine,
  RiEyeLine,
  RiEyeOffLine,
  RiLock2Line
} from 'react-icons/ri';

import {
  PasswordInput as MantinePasswordInput,
  PasswordInputProps
} from '@mantine/core';

import { useMergeClassNames } from '../../utils';

import { PasswordInputOverrideClassNames } from './PasswordInput.styles';

const customVisibilityToggleIcon: FC<{ reveal: boolean; size?: number }> = (props) =>
  props.reveal ? (
    <RiEyeOffLine size={props?.size} />
  ) : (
    <RiEyeLine size={props?.size} />
  );

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  (props, ref) => {
    const {
      leftSection,
      visibilityToggleIcon,
      error,
      rightSection,
      size = 'md',
      onFocus,
      onBlur,
      variant,
      classNames,
      ...rest
    } = props;
    const [focussed, setFocussed] = useState<boolean>(false);
    const rightSectionFinal = error ? (
      <RiErrorWarningLine />
    ) : rightSection ? (
      rightSection
    ) : undefined;

    const classes = useMemo(() => {
      return PasswordInputOverrideClassNames({
        size,
        focussed,
        error,
        variant
      });
    }, [size, focussed, error, variant]);

    const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setFocussed(true);
      onFocus && onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setFocussed(false);
      onBlur && onBlur(e);
    };

    const mergedClassNames = useMergeClassNames({
      classNames: [classes, classNames]
    });

    return (
      <MantinePasswordInput
        {...rest}
        ref={ref}
        onFocus={handleFocus}
        onBlur={handleBlur}
        leftSection={leftSection ? leftSection : <RiLock2Line />}
        visibilityToggleIcon={
          visibilityToggleIcon ? visibilityToggleIcon : customVisibilityToggleIcon
        }
        rightSection={rightSectionFinal}
        size={size}
        error={error}
        variant={variant}
        classNames={mergedClassNames}
      />
    );
  }
);

export { PasswordInput };
export type { PasswordInputProps };
